import { Moment } from 'moment-timezone';
import { z } from 'zod';

import { formatPhoneNumberForBackend } from '../../utils/formatting';
import { useAxleMutation, useAxleQuery } from '../apiHelpers';
import { Address, addressWithLatLngSchema } from '../sharedSchemas/addresses';
import { languageSchema, organizationBasicSchema, sexSchema } from '../sharedSchemas/basicSchemas';
import {
    documentWithLinkSchema,
    patientForListDisplaySchema,
    qhpForListDisplaySchema,
} from '../sharedSchemas/misc';
import { visitOrderForListDisplaySchema } from '../sharedSchemas/visitOrders';
import { visitForListSchema } from '../sharedSchemas/visits';

export const patientForDetailDisplaySchema = patientForListDisplaySchema.extend({
    addresses: z.array(addressWithLatLngSchema),
    visits: z.array(visitForListSchema),
    documents: z.array(documentWithLinkSchema),
    notes: z.string(),
    care_team: z.array(qhpForListDisplaySchema),
    service_orders: z.array(visitOrderForListDisplaySchema),
    patient_visit_list_link: z.string(),
    comms_preferences: z.object({
        allows_automated_calls: z.boolean(),
        allows_clinician_calls: z.boolean(),
        allows_automated_sms: z.boolean(),
        allows_clinician_sms: z.boolean(),
        allows_automated_emails: z.boolean(),
    }),
    permissions: z.object({
        comms: z.object({
            view: z.boolean(),
            send: z.boolean(),
        }),
    }),
});

export type PatientForDetailDisplay = z.infer<typeof patientForDetailDisplaySchema>;

export const usePatient = (patientId: string) =>
    useAxleQuery({
        queryKey: ['patient', patientId],
        path: `/patients/${patientId}`,
        suspense: true,
        responseSchema: z.object({
            patient: patientForDetailDisplaySchema,
        }),
        transform: (data) => data.patient,
    }).data;

export const usePatients = ({
    page,
    pageSize,
    searchTerm,
}: {
    page: number;
    pageSize: number;
    searchTerm: string;
}) =>
    useAxleQuery({
        queryKey: ['patients', page, pageSize, searchTerm],
        path: '/patients',
        params: {
            page: page,
            page_size: pageSize,
            search_term: searchTerm,
        },
        suspense: true,
        keepPreviousData: true,
        responseSchema: z.object({
            patients: z.array(patientForListDisplaySchema),
            num_patients: z.number(),
        }),
        transform: (data) => ({
            patients: data.patients,
            numPatients: data.num_patients,
        }),
    }).data;

export const usePatientOptions = () =>
    useAxleQuery({
        path: '/patients/options',
        suspense: true,
        responseSchema: z.object({
            languages: z.array(languageSchema),
            default_language: languageSchema,
            orgs: z.array(organizationBasicSchema),
            sexes: z.array(sexSchema),
        }),
        transform: (data) => ({
            languages: data.languages,
            defaultLanguage: data.default_language,
            orgs: data.orgs,
            sexes: data.sexes,
        }),
    }).data;

const useCreatePatientResponseSchema = z.object({
    patient_uuid: z.string().uuid(),
    address_uuid: z.string().uuid(),
});
export type UseCreatePatientResponse = z.infer<typeof useCreatePatientResponseSchema>;
export const useCreatePatient = () =>
    useAxleMutation({
        axiosConfig: ({
            orgId,
            firstName,
            lastName,
            email,
            phone,
            dob,
            sex,
            language,
            address,
            careTeam,
            externalId,
            patientMeta,
            preferredClinicianSex,
            payers,
            onSuccess,
        }: {
            orgId: string;
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
            dob: Moment;
            sex: string;
            preferredClinicianSex: string | null;
            language: string;
            address: Address;
            careTeam: string[];
            payers: string[];
            externalId?: string;
            patientMeta?: Record<string, string>;
            onSuccess: (data: z.infer<typeof useCreatePatientResponseSchema>) => void;
        }) => ({
            method: 'post',
            path: `/patients/create`,
            data: {
                org_id: orgId,
                first_name: firstName,
                last_name: lastName,
                email: email,
                phone: formatPhoneNumberForBackend(phone),
                dob: dob.format('YYYY-MM-DD'),
                sex: sex,
                preferred_clinician_sex: preferredClinicianSex,
                preferred_language: language,
                address: address,
                care_team: careTeam,
                external_patient_id: externalId,
                patient_meta: patientMeta,
                payers,
            },
            onSuccess,
        }),
        responseSchema: useCreatePatientResponseSchema,
        defaultSuccessMessage: 'Patient info saved',
        softResetQueries: [['patients']],
    });

export const useEditPatient = (patientId: string) =>
    useAxleMutation({
        axiosConfig: (data) => ({
            method: 'post',
            path: `/patients/${patientId}/edit`,
            data: data,
        }),
        softResetQueries: [['patients'], ['patient', patientId], ['availability']],
    });
